import type { CategoryDto, GymSettingsDto, TechniqueDto, VideoAssetDto } from '@bjj/shared';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api, type RequestOptions } from './client';

export const useCategories = () =>
  useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => api.request<{ items: CategoryDto[] }>('/admin/categories').then((r) => r.items),
  });

export const useTechniques = () =>
  useQuery({
    queryKey: ['admin', 'techniques'],
    queryFn: () => api.request<{ items: TechniqueDto[] }>('/admin/techniques').then((r) => r.items),
  });

export const useVideos = () =>
  useQuery({
    queryKey: ['admin', 'videos'],
    queryFn: () => api.request<{ items: VideoAssetDto[] }>('/admin/videos'),
  });

export const useAdminSettings = () =>
  useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.request<GymSettingsDto>('/admin/settings'),
  });

/**
 * Saves a new display order for `queryKey`'s list (items with `id` and `order`). The list shows the
 * new order at once and goes back to the saved order if the request fails.
 */
export function useReorder(
  queryKey: QueryKey,
  path: (groupId: string) => string,
  invalidate: QueryKey[] = [],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, ids }: { groupId: string; ids: string[] }) =>
      api.request<void>(path(groupId), { method: 'PUT', body: { ids } }),
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData(queryKey);
      const position = new Map(ids.map((id, i) => [id, (i + 1) * 10]));
      qc.setQueryData<{ id: string; order: number }[]>(queryKey, (old) =>
        old?.map((item) =>
          position.has(item.id) ? { ...item, order: position.get(item.id)! } : item,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => qc.setQueryData(queryKey, context?.previous),
    onSettled: async () => {
      await Promise.all(
        [queryKey, ...invalidate].map((key) => qc.invalidateQueries({ queryKey: key })),
      );
    },
  });
}

/** Mutation that calls the API and invalidates the given query keys on success. */
export function useApiMutation<TVars, TResult = unknown>(
  build: (vars: TVars) => { path: string } & RequestOptions,
  invalidate: QueryKey[] = [],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => {
      const { path, ...opts } = build(vars);
      return api.request<TResult>(path, opts);
    },
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => qc.invalidateQueries({ queryKey: key })));
    },
  });
}
